import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import { ThemeProvider } from 'styled-components';
import { ConfigProvider } from 'antd';
import type { AppDispatch, RootState } from '../../redux/store';
import { fetchUserProfile } from '../../redux/Settings';
import { useTheme } from '../../context/ThemeContext';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import AvatarSection from './AvatarSection';
import UserInfoSection from './UserInfoSection';
import ContactDetailsSection from './ContactDetailsSection';
import {
  ProfilePageWrapper,
  ProfilePageTitle,
  ProfileCard,
  CardSpinnerWrapper,
} from './Styles';
import { PROFILE_PAGE_TITLE, ROLES_HIDING_EDIT } from './constants';
import type { ProfileProps } from './types';

const Profile: React.FC<ProfileProps> = ({ role: roleProp }) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';

  const { user: settingsUser, loading } = useSelector((state: RootState) => state.settings);
  const { role: roleFromAuth, loginData } = useSelector((state: RootState) => state.auth);
  const role = roleProp ?? roleFromAuth ?? '';

  const user = settingsUser ?? (loginData as typeof settingsUser) ?? null;

  useEffect(() => {
    if (role) {
      dispatch(fetchUserProfile({ role }));
    }
  }, [dispatch, role]);

  const showEditButton = useMemo(() => {
    if (!role) return false;
    return !ROLES_HIDING_EDIT.includes(role as 'merchant_staff' | 'merchant_manager');
  }, [role]);

  const handleEditClick = () => {
    navigate('/settings');
  };

  const themeConfig = useMemo(
    () => ({ mode: isDarkMode ? 'dark' : 'light' } as const),
    [isDarkMode]
  );

  return (
    <ThemeProvider theme={themeConfig}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <ProfilePageWrapper>
          <ProfilePageTitle level={2}>{PROFILE_PAGE_TITLE}</ProfilePageTitle>
          <ProfileCard>
            {loading.profile && !user ? (
              <CardSpinnerWrapper>
                <Spin size="large" />
              </CardSpinnerWrapper>
            ) : (
              <>
                <AvatarSection
              name={user?.name ?? ''}
              username={user?.username ?? ''}
              showEditButton={showEditButton}
              onEditClick={handleEditClick}
            />
            <UserInfoSection
              name={user?.name ?? ''}
              username={user?.username ?? ''}
              role={role}
              address={user?.address}
            />
                <ContactDetailsSection user={user} role={role} />
              </>
            )}
          </ProfileCard>
        </ProfilePageWrapper>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default React.memo(Profile);
